import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);
const adminIdentity = (value) => Boolean(
  value && typeof value === "object" && !Array.isArray(value)
  && value.role === "admin" && typeof value.id === "string" && value.id.trim()
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [sessionState, setSessionState] = useState("resolving");
  const mounted = useRef(false);
  const generation = useRef(0);
  const validation = useRef(null);

  const settle = useCallback((identity, state) => {
    setUser(identity);
    setSessionState(state);
    setReady(true);
  }, []);

  const retrySession = useCallback(() => {
    if (!mounted.current) return Promise.resolve(false);
    const token = localStorage.getItem("cc_token");
    if (validation.current?.generation === generation.current
        && validation.current.token === token) return validation.current.promise;
    const attempt = ++generation.current;
    setUser(null);
    setSessionState("resolving");
    setReady(false);
    if (!token) {
      settle(false, "required");
      return Promise.resolve(false);
    }
    const applicable = () => {
      if (!mounted.current || generation.current !== attempt) return false;
      const currentToken = localStorage.getItem("cc_token");
      if (currentToken !== token) {
        settle(currentToken ? null : false, currentToken ? "unavailable" : "required");
        return false;
      }
      return true;
    };
    // One ordinary identity request. Further attempts require a user action.
    const promise = Promise.resolve().then(async () => {
      try {
        if (!applicable()) return false;
        const { data } = await api.get("/auth/me");
        if (!applicable()) return false;
        if (!adminIdentity(data)) {
          localStorage.removeItem("cc_token");
          settle(false, "required");
          return false;
        }
        settle(data, "authenticated");
        return data;
      } catch (error) {
        if (!applicable()) return false;
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem("cc_token");
          settle(false, "required");
        } else {
          // Keep the credential private, but grant no unvalidated privilege.
          settle(null, "unavailable");
        }
        return false;
      }
    }).finally(() => {
      if (validation.current?.generation === attempt) validation.current = null;
    });
    validation.current = { generation: attempt, token, promise };
    return promise;
  }, [settle]);

  const stopValidation = useCallback(() => {
    mounted.current = false;
    generation.current++;
    validation.current = null;
  }, []);

  useEffect(() => {
    mounted.current = true;
    retrySession();
    return stopValidation;
  }, [retrySession, stopValidation]);

  const login = async (email, password) => {
    const attempt = ++generation.current;
    validation.current = null;
    const tokenBefore = localStorage.getItem("cc_token");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (!mounted.current || generation.current !== attempt
          || localStorage.getItem("cc_token") !== tokenBefore) {
        throw new Error("Sign-in request is no longer current.");
      }
      if (typeof data?.token !== "string" || !data.token.trim() || !adminIdentity(data.user)) {
        if (localStorage.getItem("cc_token") === tokenBefore) localStorage.removeItem("cc_token");
        settle(false, "required");
        throw new Error("Unable to complete sign in.");
      }
      localStorage.setItem("cc_token", data.token);
      settle(data.user, "authenticated");
      return data.user;
    } catch (error) {
      if (mounted.current && generation.current === attempt) {
        const currentToken = localStorage.getItem("cc_token");
        settle(currentToken ? null : false, currentToken ? "unavailable" : "required");
      }
      throw error;
    }
  };

  const logout = () => {
    generation.current++;
    validation.current = null;
    localStorage.removeItem("cc_token");
    settle(false, "required");
  };

  return (
    <AuthContext.Provider value={{ user, ready, sessionState, retrySession, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
